import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  resource,
  Signal,
  signal,
} from '@angular/core';
import { SimpleApi } from '@core/api/simple.api';
import { appConfig } from '@core/app-config';
import { AuthService } from '@core/auth/auth.service';
import { RettighedsType } from '@core/auth/rettighed.model';
import { TableRowDirective } from '@core/primevue/directives/table-row.directive';
import { NgxFilesizeModule } from 'ngx-filesize';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbItemClickEvent, BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ServerFil } from './server-fil.model';

@Component({
  selector: 'app-filer',
  imports: [
    BreadcrumbModule,
    NgxFilesizeModule,
    TableModule,
    TableRowDirective,
    TableRowDirective,
    ButtonModule,
    FileUploadModule,
    DatePipe,
    TooltipModule,
    ConfirmDialog,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './filer.component.html',
  styleUrl: './filer.component.css',
})
export class FilerComponent {
  confirmationService = inject(ConfirmationService);
  messageService = inject(MessageService);
  authService = inject(AuthService);
  simpleApi = inject(SimpleApi);
  breadcrumbs = signal<MenuItem[]>([]);
  selectedFile = signal<ServerFil | undefined>(undefined);
  apiRoot = appConfig.apiRoot;
  home = computed(() => ({
    label: this.rootFolder
      .value()
      ?.replaceAll('\\\\', '\\')
      .replaceAll('"', '')
      .replaceAll('/', ''),
  }));

  allowDelete = computed(() =>
    this.authService.hasRettighed(RettighedsType.AdminFilerSlet)
  );

  path = computed(() =>
    this.breadcrumbs()
      .map(x => x.label)
      .join('/')
  );

  data = resource({
    params: () => this.path(),
    loader: async ({ params, abortSignal }) => {
      return await this.simpleApi.getList<ServerFil>(
        '/api/admin/serverfiler',
        {
          path: params,
        },
        abortSignal
      );
    },
  });

  rootFolder = resource({
    loader: async ({ abortSignal }) => {
      const response = await this.simpleApi.getRaw<string>(
        '/api/admin/serverfiler/root',
        {},
        abortSignal
      );
      return response;
    },
  });

  tableData: Signal<ServerFil[]> = computed(() => this.data.value() || []);

  up() {
    this.breadcrumbs.update(v => v.slice(0, -1));
  }

  async onRowClick(file: ServerFil) {
    if (file.isFolder) {
      if (file.filename === '\\..') {
        this.breadcrumbs.update(v => v.slice(0, -1));
      } else {
        this.breadcrumbs.update(v => [...v, { label: file.filename }]);
      }
    } else {
      await this.getFile(file);
    }
  }

  async onClickDowload(e: MouseEvent, item: ServerFil) {
    e.stopPropagation();
    await this.getFile(item, true);
  }

  async getFile(file: ServerFil, download = false) {
    if (file.size > 10000000) {
      alert('Filen er for stor');
      return;
    }
    const blob = await this.simpleApi.getBlob('/api/admin/serverfiler/fil', {
      path: this.path() + '/' + file.filename,
      dowload: download,
    });
    const objectUrl = window.URL.createObjectURL(blob);
    if (download) {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.setAttribute('download', file.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      window.open(objectUrl);
    }
  }

  async onClickDelete(e: MouseEvent, item: ServerFil) {
    e.stopPropagation();

    this.confirmationService.confirm({
      target: e.target as EventTarget,
      message: 'Vil du slette filen ' + item.filename + '?',
      header: 'Slet fil',
      rejectButtonProps: {
        label: 'Fortryd',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Slet',
        severity: 'danger',
      },

      accept: async () => {
        await this.simpleApi.getSingle('/api/admin/serverfiler/delete', {
          path: this.path() + '/' + item.filename,
        });
        this.data.value.update(files => [
          ...files!.filter(x => x.filename != item.filename),
        ]);
      },
    });
  }

  onBreadCrumbClick(event: BreadcrumbItemClickEvent) {
    const clickedLabel = event.item.label;

    if (clickedLabel?.startsWith('\\\\')) {
      this.breadcrumbs.set([]);
      return;
    }

    const newPath: MenuItem[] = [];
    let matched = false;
    this.breadcrumbs().forEach(x => {
      if (!matched) {
        newPath.push(x);
        if (x.label === clickedLabel) {
          matched = true;
        }
      }
    });
    this.breadcrumbs.set(newPath);
  }

  onBasicUploadAuto() {
    this.messageService.add({
      severity: 'info',
      summary: 'Success',
      detail: 'File Uploaded',
    });
  }

  uploadAllowed = this.authService.hasRettighed(
    RettighedsType.AdminFilerUpload
  );

  async uploadHandler(event: FileUploadHandlerEvent) {
    if (event.files.length > 0) {
      const file = event.files[0];
      const existingFiles = this.data.value() || [];

      if (existingFiles.filter(f => f.filename === file.name).length > 0) {
        this.confirmationService.confirm({
          message:
            'Filen <b>' +
            file.name +
            '</b> findes allerede. Vil du overskrive den?',
          header: 'Upload fil',
          rejectButtonProps: {
            label: 'Fortryd',
            severity: 'secondary',
            outlined: true,
          },
          acceptButtonProps: {
            label: 'Upload',
            severity: 'primary',
          },

          accept: async () => {
            await this.doUpload(file);
          },
        });
      } else {
        await this.doUpload(file);
      }
    }
  }

  async doUpload(file: File) {
    const formData = new FormData();

    formData.append('files', file);
    formData.append('path', this.path());
    await this.simpleApi.createMultipart(
      '/api/admin/serverfiler/upload',
      formData
    );
    this.data.reload();
  }
}
